import { useCallback, useState } from "react";
import type { ReactNode } from "react";
import { Container, Grid, Typography } from "@mui/material";
import InformationBox from "components/InformationBox";
import PageTitle from "components/PageTitle";
import LoadingScreen from "components/Template/LoadingScreen";
import { PerimetreIcon } from "icons";
import { useNavigate } from "react-router";
import { withPageErrorBoundary, useSnackbarErrorHandler, SdeappsError } from "utils/errorHandling";
import TransfertEditionToolbar from "./components/TransfertEditionToolbar";
import PerimetreEditTransfertsList from "./components/PerimetreEditTransfertsList";
import {
  TransfertsEditionProvider,
  useTransfertsEditionContext,
} from "./store/transfertsEditionContext";
import useTransfertsEditionViewModelLoader from "./hooks/useTransfertsEditionViewModelLoader";
import TransfertEditionConfirmationDialog from "./components/TransfertEditionConfirmationDialog";
import { dateUtil } from "@sdeapps/react-core";
import ToastMessages from "constants/ToastMessages";
import { enqueueSnackbar } from "notistack";
import { transfertsService, networkService } from "services";
import { TransfertsEditionActionType } from "./store/transfertsEditionTypes";
import { routesConfig } from "config/app-config";
import type { SuppressionTransfertData } from "models";
import { useRequiredParams } from "hooks";

const anneeMoisJourFormat = "yyyy-MM-dd";

/** Retourne `date` formattée en string à moins que la date soit null/undefined */
function dateAsStringWhenNotUndefined(date?: Date): string | undefined {
  return date != null ? dateUtil.format(date, anneeMoisJourFormat) : undefined;
}

/**
 * Compare une date nullable avec une date formattée `anneeMoisJourFormat`.
 * Si la nouvelle date est nulle, c'est qu'il n'y a pas de changement à surveiller.
 *
 * **Postulat : on ne peut pas modifier une date existante pour la rendre nulle**
 */
function isDateUnchangedOrUndefined(
  newDate: Date | undefined,
  actualDate: string | undefined
): boolean {
  return newDate == null || actualDate === dateUtil.format(newDate, anneeMoisJourFormat);
}

async function waitForDataReplication(
  actionUtilisateur: TransfertsEditionActionType,
  updatedTransfertsIds: Array<string>,
  identifiants: {
    idPerimetre: string;
    idCible: string | undefined;
  },
  datesModifiees: {
    dateEffet?: Date;
    dateDebut?: Date;
    dateDeliberation?: Date;
    datePremierTransfert?: Date;
    dateAssembleeGenerale?: Date;
  }
): Promise<void> {
  const { idPerimetre, idCible } = identifiants;
  const { dateEffet, dateDebut, dateDeliberation, datePremierTransfert, dateAssembleeGenerale } =
    datesModifiees;

  let transferts;
  let isReplicationInProgress = true;

  switch (actionUtilisateur) {
    case TransfertsEditionActionType.ReaffectationEPCI:
    case TransfertsEditionActionType.DirectIndirect:
    case TransfertsEditionActionType.IndirectDirect:
      // Tester que les transferts ont bien la bonne nouvelle valeur d'idEpci
      // ainsi que la bonne date d'effet
      transferts = await transfertsService.getByPerimetre(idPerimetre);
      isReplicationInProgress = transferts
        .filter((transfert) => updatedTransfertsIds.includes(transfert.id))
        .some(
          (transfert) =>
            transfert.idEpci !== idCible &&
            transfert.dateDebut !== dateUtil.format(dateEffet, anneeMoisJourFormat)
        );
      break;
    case TransfertsEditionActionType.ModificationDates: {
      // Tester et confirmer que les transferts modifiés ont bien les nouvelles dates
      // renseignées dans l'interface utilisateur
      transferts = await transfertsService.getByPerimetre(idPerimetre);
      isReplicationInProgress = !transferts
        .filter((transfert) => updatedTransfertsIds.includes(transfert.id))
        .every(
          (transfert) =>
            isDateUnchangedOrUndefined(dateDebut, transfert.dateDebut) &&
            isDateUnchangedOrUndefined(dateDeliberation, transfert.dateDeliberation) &&
            isDateUnchangedOrUndefined(datePremierTransfert, transfert.datePremierTransfert) &&
            isDateUnchangedOrUndefined(dateAssembleeGenerale, transfert.dateAssembleeGenerale)
        );
      break;
    }
    case TransfertsEditionActionType.SuppressionTransfert:
      // Tester que les transferts n'apparaissent plus sur le périmètre
      transferts = await transfertsService.getByPerimetre(idPerimetre);
      isReplicationInProgress = transferts.some((transfert) =>
        updatedTransfertsIds.includes(transfert.id)
      );
      break;
    case TransfertsEditionActionType.ReaffectationPerimetre:
      // Tester que les transferts n'apparaissent plus sur le périmètre d'origine
      transferts = await transfertsService.getByPerimetre(idPerimetre);
      isReplicationInProgress = transferts.some((transfert) =>
        updatedTransfertsIds.includes(transfert.id)
      );

      if (isReplicationInProgress) break;

      // Puis tester que les transferts apparaissent bien sur le périmètre de destination
      if (idCible != null) {
        transferts = await transfertsService.getByPerimetre(idCible);
        isReplicationInProgress =
          transferts.length === 0 ||
          transferts.some((transfert) => !updatedTransfertsIds.includes(transfert.id));
      }
      break;
    default:
      throw new SdeappsError("Action non implémentée");
  }

  if (isReplicationInProgress) {
    throw new SdeappsError(
      `Les modifications du périmètre ${idPerimetre} n'ont pas encore été répliquées`
    );
  }
}

function PerimetreEditTransferts(): ReactNode {
  const navigate = useNavigate();
  const { id: idPerimetre } = useRequiredParams<{ id: string }>();
  useTransfertsEditionViewModelLoader(idPerimetre);
  const {
    state: {
      perimetre,
      selectedTransferts,
      filteredTransferts,
      actionUtilisateur,
      actionParametres,
    },
  } = useTransfertsEditionContext();
  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] = useState(false);
  const { catchErrors: catchApiModificationErrors, isLoading: isSendingApiModifications } =
    useSnackbarErrorHandler();

  const getSuppressionTransfertData = useCallback(
    (idTransfert: string): SuppressionTransfertData => ({
      idCommune: filteredTransferts.find((t) => t.id === idTransfert)?.idCommune ?? "",
      idTransfert,
    }),
    [filteredTransferts]
  );

  const callGestionApi = useCallback(() => {
    const {
      idCible,
      dateEffet,
      dateDebut,
      dateDeliberation,
      datePremierTransfert,
      dateAssembleeGenerale,
    } = actionParametres;

    void catchApiModificationErrors(async () => {
      switch (actionUtilisateur) {
        case TransfertsEditionActionType.ReaffectationEPCI:
        case TransfertsEditionActionType.DirectIndirect:
        case TransfertsEditionActionType.IndirectDirect:
          await transfertsService.changeEpci({
            idEpci: idCible,
            dateEffet: dateUtil.format(dateEffet, anneeMoisJourFormat),
            transfertIds: selectedTransferts,
          });
          break;
        case TransfertsEditionActionType.ModificationDates:
          await transfertsService.modificationDates({
            transfertIds: selectedTransferts,
            dateDebut: dateAsStringWhenNotUndefined(dateDebut),
            dateDeliberation: dateAsStringWhenNotUndefined(dateDeliberation),
            datePremierTransfert: dateAsStringWhenNotUndefined(datePremierTransfert),
            dateAssembleeGenerale: dateAsStringWhenNotUndefined(dateAssembleeGenerale),
          });
          break;
        case TransfertsEditionActionType.SuppressionTransfert:
          await transfertsService.suppressionTransferts(
            selectedTransferts.map(getSuppressionTransfertData)
          );
          break;
        case TransfertsEditionActionType.ReaffectationPerimetre:
          if (idCible != null) {
            await transfertsService.changePerimetre({
              idPerimetre: idCible,
              dateEffet: dateUtil.format(dateEffet, anneeMoisJourFormat),
              transfertIds: selectedTransferts,
            });
          } else {
            throw new SdeappsError("Un id de Périmètre de réaffectation est nécessaire");
          }
          break;
        default:
          // Action non gérée ou traitée, pas d'appels affectués
          throw new SdeappsError("Action non implémentée");
      }

      await networkService.waitForReplication(
        async () => {
          await waitForDataReplication(
            actionUtilisateur,
            selectedTransferts,
            {
              idPerimetre,
              idCible,
            },
            {
              dateEffet,
              dateDebut,
              dateDeliberation,
              datePremierTransfert,
              dateAssembleeGenerale,
            }
          );
        },
        20,
        () => {
          navigate(routesConfig.perimetre.getParameterPath(idPerimetre));
        },
        () => {
          enqueueSnackbar({
            autoHideDuration: 8000,
            variant: "warning",
            message: ToastMessages.LONG_REPLICATION,
          });
        }
      );

      setIsConfirmationDialogOpen(false);
    });
  }, [
    actionParametres,
    idPerimetre,
    catchApiModificationErrors,
    actionUtilisateur,
    selectedTransferts,
    getSuppressionTransfertData,
    navigate,
  ]);

  if (perimetre == null) {
    return <LoadingScreen />;
  }

  return (
    <Container maxWidth={false}>
      <Grid container spacing={2}>
        <PageTitle
          title={perimetre.libelle}
          icon={<PerimetreIcon />}
          subtitle="Périmètre : modification des transferts"
          competences={perimetre.competence}
        />
        <Grid container size={12}>
          <InformationBox
            hasBackground
            articleTitle="Modification des transferts"
            noChildrenPadding>
            <Grid container spacing={0}>
              <TransfertEditionConfirmationDialog
                onConfirm={callGestionApi}
                onCancel={() => {
                  setIsConfirmationDialogOpen(false);
                }}
                open={isConfirmationDialogOpen}
                isSending={isSendingApiModifications}
              />
              <TransfertEditionToolbar
                onSave={() => {
                  setIsConfirmationDialogOpen(true);
                }}
                isSending={isSendingApiModifications}
              />
              {actionUtilisateur != null ? (
                <PerimetreEditTransfertsList />
              ) : (
                <Grid size={12} sx={{ p: 2 }}>
                  <Typography sx={{ textAlign: "center" }}>
                    Veuillez sélectionner une action pour afficher la liste de transferts éligibles
                  </Typography>
                </Grid>
              )}
            </Grid>
          </InformationBox>
        </Grid>
      </Grid>
    </Container>
  );
}

function PerimetreEditTransfertsWithContext(): ReactNode {
  return (
    <TransfertsEditionProvider>
      <PerimetreEditTransferts />
    </TransfertsEditionProvider>
  );
}

const PerimetreEditTransfertsWithErrorBoundary = withPageErrorBoundary(
  PerimetreEditTransfertsWithContext
);

export default PerimetreEditTransfertsWithErrorBoundary;
